import { useRef, useState } from "react";
import { NDT_KART_SIRASI, hedefEsit, kartSirasiyla, ndtEtiketi, type GelenHedef, type NdtKarti, type NdtKartRaporu } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { api } from "@/lib/api";
import {
  OKUMA_UCU,
  ndtDurumMetni,
  ndtImzacilari,
  ndtNedenMetni,
  ndtNotMetni,
  ndtRaporSatiri,
  ndtSayfaMetni,
  ndtYerdekiler,
  okumaDugmesi,
  okumaEylemleri,
} from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 2 · PR 9: Documents kartının NDT "Document reading" bölümü (prototip belgeler--ndt-okuma). Okuma dosyayı raporlarına böler: rapor
 * başına sayfa aralığı, OASIS'in önerdiği ya da kişinin seçtiği yer, bekleme durumu ("Waiting for its riser", "Waiting for the step", "Needs
 * owner"), değerler, sonuç satırları ve uyarılar. "Use this place" kartın bugünkü tür+yer kaydını çağırır (yeri kişi kaydeder; okuma yazmaz);
 * "Use these pages" ve "Use this signer" yalnız formu doldurur. Hiçbir şey kişi "Confirm and file" demeden dosyalanmaz; okunan değer hiçbir
 * alana kendiliğinden dolmaz. Uyarıların kararı PR 8'in kutusudur (`UyariSatiri`). `salt` kapanmış satır içindir: düğme ve karar kutusu yok.
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";

/** Sayfa aralığının metni ("3" ya da "1-2"). */
const aralik = (s: { bas: number; son: number }) => (s.bas === s.son ? String(s.bas) : `${s.bas}-${s.son}`);
/** Uyarı kartının riser ve hattı (KIM-02 açıklaması): hat_ndt'de yerin kendisi; adımda yer metninin riseri ve uyarının hattı. */
const uyariYeri = (r: NdtKartRaporu, hat: string | null): { rn: string; hat: string } | null => {
  if (r.yer?.tur === "hat_ndt") return { rn: r.yer.rn, hat: r.yer.hat };
  const rn = r.yerMetni?.split(" · ")[0];
  return rn && hat ? { rn, hat } : null;
};
const durumTonu = (r: NdtKartRaporu) => (r.durum === "kisi" ? "ok" : r.durum === "oneri" ? "info" : r.durum === "yok" ? "neutral" : "warn");

export interface NdtFormYardimi {
  /** formun seçili türü */
  tur: string;
  /** "Use this place": bugünkü tür+yer kaydı */
  yerKaydet: (h: GelenHedef) => Promise<void>;
  /** "Use these pages": Pages alanı (Partial) */
  sayfalar: (s: string) => void;
  /** "Use this signer": imzalayan seçimi; imzalayan sorulmayan adımda null */
  imzalayanlar: ((ids: string[]) => void) | null;
}

export function NdtOkumaBolumu({ satirId, k, onYenile, salt, hedef, form }: { satirId: string; k: NdtKarti; onYenile: () => void; salt?: boolean; hedef: GelenHedef | null; form?: NdtFormYardimi }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const yerdekiler = new Set(ndtYerdekiler(k, hedef));
  const ilkAcik = k.raporlar.find((r) => yerdekiler.has(r.sira))?.sira ?? k.raporlar[0]?.sira ?? null;
  const sayfaMetni = yardim ? ndtSayfaMetni(k, hedef, yardim.tur) : null;
  const imzacilar = yardim?.imzalayanlar ? ndtImzacilari(k, hedef) : [];
  const toplamUyari = k.raporlar.reduce((t, r) => t + r.uyarilar.length, 0) + (k.karsilastirma?.uyarilar.length ?? 0);
  const deger = (r: NdtKartRaporu, alan: string) => r.degerler.find((d) => d.alan === alan)?.deger ?? null;

  return (
    <div data-bolum="ndt-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.kod && <p className="text-xs text-ink-3">{cevir("Reading job {0}", k.kod)}</p>}
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads each report of the file for you to check. Nothing is filed or changed.")}</p>}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && k.raporlar.length > 0 && (
        <>
          <AltBaslik>{cevir("Reports in this file")}</AltBaslik>
          <ul className="divide-y divide-line rounded-md border border-line text-sm" data-bolum="ndt-raporlar">
            {k.raporlar.map((r) => (
              <li key={r.sira} className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 px-2.5 py-2 @min-[40rem]:grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1.3fr)]" data-rapor={r.sira}>
                <span>
                  <span className="block text-xs text-ink-3">{cevir("Pages")}</span>
                  <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: r.sayfalar.bas })} target="_blank" rel="noreferrer">p.{aralik(r.sayfalar)}</a>
                </span>
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  <span className="block font-medium">{cevir("Report")} {r.sira}{r.raporNo ? ` · ${r.raporNo}` : ""}</span>
                  <span className="block text-xs text-ink-3">{ndtRaporSatiri(r) || "—"}</span>
                </span>
                <span className="col-span-2 min-w-0 [overflow-wrap:anywhere] @min-[40rem]:col-span-1">
                  <span className="block text-xs text-ink-3">{cevir("Place")}</span>
                  {r.yerMetni && <span className="mr-1.5">{r.yerMetni}</span>}
                  {r.durum !== "yok" ? <Etiket tone={durumTonu(r)}>{ndtDurumMetni(r)}</Etiket> : <span className="text-ink-2">{r.neden ? ndtNedenMetni(r.neden) : "—"}</span>}
                  {r.durum === "oneri" && <span className="mt-0.5 block text-xs text-ink-3">{cevir("Proposed from riser {0}, SN {1} and the stage.", deger(r, "riser_no") ?? "—", deger(r, "hat_sn") ?? deger(r, "diger_sn") ?? "—")}</span>}
                  {r.imzaci && <span className="mt-0.5 block text-xs text-ink-3">{cevir("Signer {0} matches NDT inspector {1}.", deger(r, "muayeneci") ?? "—", r.imzaci.ad)}</span>}
                  {r.etkiler.includes("kayit_yok_kalkar") && <span className="mt-0.5 block text-xs text-warn">{cevir("The line has “No record available”: filing this report clears it.")}</span>}
                  {r.etkiler.includes("adim_raporu_var") && <span className="mt-0.5 block text-xs text-ink-3">{cevir("The step already has a report; this one is added to its reports.")}</span>}
                  {yardim && r.durum === "oneri" && r.yer && r.yer.tur === yardim.tur && !hedefEsit(r.yer, hedef) && (
                    <Buton kucuk className="mt-1" disabled={mesgul !== null} mesgul={mesgul === `yer-${r.sira}`} onClick={() => calistir(`yer-${r.sira}`, () => yardim.yerKaydet(r.yer!))}>{cevir("Use this place")}</Buton>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {yardim && (sayfaMetni || imzacilar.length > 0) && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {sayfaMetni && <Buton kucuk disabled={mesgul !== null} onClick={() => yardim.sayfalar(sayfaMetni)}>{cevir("Use these pages")}</Buton>}
              {imzacilar.length > 0 && <Buton kucuk disabled={mesgul !== null} onClick={() => yardim.imzalayanlar?.(imzacilar)}>{cevir("Use this signer")}</Buton>}
              <span className="text-xs text-ink-3">{cevir("Nothing is filed until you confirm.")}</span>
            </div>
          )}

          {k.raporlar.map((r) => {
            return (
              <details key={r.sira} className="mt-2 rounded-md border border-line px-2.5 py-1.5" open={r.sira === ilkAcik} data-rapor-ayrinti={r.sira}>
                <summary className="cursor-pointer text-sm font-medium">{cevir("Report {0} · {1} · p.{2}", r.sira, r.raporNo ?? "—", aralik(r.sayfalar))}</summary>
                <ul className="mt-1.5 space-y-0.5 text-sm">
                  {kartSirasiyla(r.degerler, NDT_KART_SIRASI).map((d) => (
                    <li key={d.alan} className="flex gap-2">
                      <Nokta k={d.kesinlik} />
                      <span className="w-[11rem] shrink-0 text-ink-2">{cevir(ndtEtiketi(d.alan))}</span>
                      <span className="min-w-0 [overflow-wrap:anywhere]">
                        {d.deger ?? <span className="text-ink-3">{cevir("not read")}</span>}
                        {d.sayfa !== null && <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: d.sayfa })} target="_blank" rel="noreferrer">p.{d.sayfa}</a></>}
                        {d.yazildigi && d.yazildigi !== d.deger && <span className="block text-xs text-ink-3">{cevir("as written: {0}", d.yazildigi)}</span>}
                      </span>
                    </li>
                  ))}
                  {r.sonuclar.map((x, i) => (
                    <li key={`s-${i}`} className="flex gap-2">
                      <Nokta k={x.kesinlik} />
                      <span className="w-[11rem] shrink-0 text-ink-2">{cevir("Result")}</span>
                      <span className="min-w-0 [overflow-wrap:anywhere]">
                        {[x.yer, x.sonuc === "kabul" ? cevir("accept") : x.sonuc === "ret" ? cevir("reject") : null].filter(Boolean).join(" · ") || <span className="text-ink-3">{cevir("not read")}</span>}
                        {x.sayfa !== null && <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: x.sayfa })} target="_blank" rel="noreferrer">p.{x.sayfa}</a></>}
                        {x.yazildigi && x.yazildigi !== x.yer && <span className="block text-xs text-ink-3">{cevir("as written: {0}", x.yazildigi)}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
                {r.uyarilar.length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {r.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={uyariYeri(r, u.hat)} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                  </ul>
                )}
              </details>
            );
          })}

          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
          <p className="mt-1 text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {(k.karsilastirma.notlar.length > 0 || k.karsilastirma.gizliKisiUyarisi > 0) && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {k.karsilastirma.notlar.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.tur === "suphe" ? "suphe" : "bos"} />
                  <span className="min-w-0">{ndtNotMetni(n)}</span>
                </li>
              ))}
              {k.karsilastirma.gizliKisiUyarisi > 0 && (
                <li className="flex gap-2"><Nokta k="bos" /><span className="min-w-0">{cevir("{0} signer alerts are shown to the roles that file NDT reports.", k.karsilastirma.gizliKisiUyarisi)}</span></li>
              )}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && toplamUyari === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : toplamUyari === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : k.karsilastirma.uyarilar.length > 0 && (
                <ul className="space-y-1.5">
                  {k.karsilastirma.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                </ul>
              )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(OKUMA_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(OKUMA_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(OKUMA_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <p className="mt-2 text-sm text-bad">{hata}</p>}
    </div>
  );
}
